import React from 'react';
import { getYear } from 'date-fns';
import staticUrls from '@finsight/urls/static';
import config from '@finsight/config';

import staticTemplateStyles from '@/finsight/ui/components/staticPages/StaticTemplate/staticTemplate.module.scss';

const Terms = () => (
  <>
    <p>
      Finsight's products and services are provided by Finsight Group, Inc (“Finsight”, “We”, “Our”, “Us”).
      These Terms of Use ("Terms") govern your access to all products and services offered by Finsight including
      Allocate, CapEdge.com, CreditFlowResearch.com, DealRoadshow.com, DealVDR.com, Evercall.co, InvestorSet.com,
      LoanSend.com, QuantumCF.com, ResearchRoom.com, SwiftBridge.com, VeriSend.com and 17g5.com (the “Sites”).
      Please read these Terms carefully, and contact us if you have any questions.
      By accessing or using our Sites, you represent that you are over the age of 18, and you agree to be bound by
      these Terms and by our Privacy Policy. SwiftBridge.com is operated by SwiftBridge LLC, a wholly-owned
      subsidiary of Finsight Group, Inc. References in these Terms to “Finsight,” “We,” “Our” and “Us” include
      SwiftBridge LLC and Finsight's other affiliates that provide the Sites. QuantumCF.com is operated by
      QuantumCF Inc., a wholly-owned affiliate of Finsight Group, Inc. that invoices and contracts with its
      customers separately from Finsight Group, Inc.
    </p>
    <p>
      THE PRINTING, COPYING, SCREENSHOTTING, DOWNLOADING, OR DISTRIBUTION OF ANY MATERIAL OTHER THAN
      DOCUMENTS EXPRESSLY PERMITTED TO BE DOWNLOADED IS STRICTLY PROHIBITED.
    </p>
    <h3>Scope of SwiftBridge Services</h3>
    <p>
      SwiftBridge.com provides telecommunications connectivity, including operator-assisted conference calling and
      related services (“Telecommunications Services”), together with certain software applications, APIs, and
      related SaaS features that complement or support those services. Telecommunications Services are subject to
      the additional terms set out in the “Universal Service Fund (USF)” and “Regulatory Compliance” sections below.
    </p>
    <h3>Disclaimer</h3>
    <p>
      NOTHING CONTAINED ON OUR SITES CREATES ANY CONTRACT OR RIGHT OF ACTION AGAINST FINSIGHT. IT IS OFFERED
      SOLELY AS A SERVICE TO CURRENT AND POTENTIAL CUSTOMERS OF FINSIGHT AND WE MAKE NO WARRANTIES, EXPRESS
      OR IMPLIED, REGARDING THE INFORMATION CONTAINED HEREIN.
    </p>
    <p>
      Information found on our Sites are not a solicitation or recommendation to buy, sell or hold securities.
      Finsight is not offering securities for sale. An offer to buy or sell can be made only with accompanying
      disclosure documents and only in the states and provinces for which they are approved. Finsight is not
      responsible for the content uploaded by clients or unaffiliated third-parties. Always verify claims, do
      your own due diligence and seek the advice of your financial adviser or counsel.
    </p>
    <p>
      All statements and expressions are the sole opinion of the editor and are subject to change without notice.
      Finsight is not liable for any investment decisions made by users of our Sites. It is strongly recommended
      that any purchase or sale decision be discussed with a financial adviser, or a broker-dealer, or a member
      of any financial regulatory bodies. The information contained herein has been provided as an information
      service only. The accuracy or completeness of the information is not warranted and is only as reliable as
      the sources from which it was obtained. Investors are cautioned that there are risks involved in any
      investment and they may lose all or a portion of funds invested.
    </p>
    <p>
      Information contained on our Sites, on Finsight's affiliated sites or third-party content and linked site(s)
      may contain "forward looking statements" within the meaning of Section 27A of the Securities Act of 1933, as
      amended and Section 21E of the Securities and Exchange Act of 1934, as amended. Any statements that express
      or involve discussions with respect to predictions, expectations, beliefs, plans, projections, objectives,
      goals, assumptions or future events or performance are not statements of historical facts and may be
      "forward
      looking statements". Forward looking statements are based on expectations, estimates and projections at the
      time the statements are made that involve a number of risks and uncertainties which could cause actual
      results
      or events to differ materially from those presently anticipated. Forward looking statement may be identified
      through the use of words such as "expects", "will", "anticipates", "estimates", "believes", or by statements
      indicating certain actions "may", "could", "should" or "might" occur.
    </p>
    <h3>Data Protection And Security</h3>
    <p>
      Finsight implements many security procedures to help protect your data. This includes, but is not limited
      to:
    </p>
    <ul className={ staticTemplateStyles.bulletList }>
      <li>Storing data in multi-tenant storage systems accessible to clients only via the Finsight platforms</li>
      <li>Encrypting data in transit using 256-bit TLS transmission</li>
      <li>
        Hosting server infrastructure in industry leading, SOC-2 certified data centers in the U.S. with the
        highest levels of physical and network security
      </li>
      <li>Having all employee undergo rigorous background checks and security awareness training</li>
      <li>
        Third-party privacy compliance through US-EU Data Privacy Framework ("DPF") and US-Swiss
        Safe Harbor, as described in our <a href={ staticUrls.getPrivacyPolicyUrl() }>Privacy Policy</a>.
      </li>
    </ul>
    <h3>European Economic Area Privacy And Data Protection</h3>
    <p>
      You are responsible for complying with applicable privacy and data protection laws, including the EU
      Data Protection Directive 95/46/EC as implemented into national law and as may be amended from time to time,
      including by the EU General Data Protection Regulation 2016/679, the Swiss Federal Act on Data Protection
      and the e-Privacy Directive 2002/58/EC as implemented into national law and as amended and replaced from
      time
      to time (together “Applicable Privacy and Data Protection Laws”), when processing personal data that
      originates in the European Economic Area (“EEA”) and is included in the documents available on the Sites or
      you upload into the Sites (together “EEA Personal Data”). In particular, as a data controller, you are
      responsible for providing information to, collecting consent of, and providing access to individuals when
      and as required under Applicable Privacy and Data Protection Laws. As a data processor, Finsight only acts
      on your behalf and under your instructions when processing EEA Personal Data. You authorize Finsight to
      transfer EEA Personal Data to the U.S. and to disclose EEA Personal Data onward to other users and service
      providers, as necessary to provide the Sites. Finsight has certified to the Data Privacy Framework for the purpose
      of providing an adequate level of protection to EEA Personal Data, and will comply with the Data Privacy Framework
      (or with any other data transfer mechanism that provides an adequate level of protection under
      Applicable Privacy and Data Protection Law if it decides to end its participation in the Data Privacy Framework)
      when processing such data.
    </p>
    <p>
      If you obtain or process EEA Personal Data from other users via the Sites, you agree to only process
      them as necessary to review and evaluate the information included on the Sites. You agree to protect
      EEA Personal Data with at least the same level of protection as provided in the Data Privacy Framework,
      which are available&nbsp;
      <a
        href="https://www.privacyshield.gov/article?id=Requirements-of-Participation"
        target="_blank"
      >
        here
      </a>.
      You will notify Finsight if you make a determination that
      you
      cannot meet this
      obligation, in which case you will cease the processing or take reasonable and appropriate steps to
      remediate the situation.
    </p>
    <h3>Account Information</h3>
    <p>
      You are responsible for maintaining the confidentiality of any usernames or passwords associated with any
      account you use to access our Sites, for monitoring all activity under the account, and you agree to assume
      full responsibility for all activities that occur under your account (unless such activities result
      from a security breach for which Finsight is responsible).&nbsp;You may not disclose or share your username
      or password with any third-parties or use them for any unauthorized purpose.&nbsp;If you become aware of any
      unauthorized use of your password or your account, or any system failures, you agree to notify Finsight
      immediately at
      <a href={ `mailto:${ config.supportEmail }` }> { config.supportEmail }</a>.
    </p>
    <p>
      When you create an account with Us, you must provide Us information that is accurate,
      complete, and current at all times.
      Failure to do so constitutes a breach of these Terms,
      which may result in immediate termination of your account on Our Sites.
    </p>
    <h3>Trademarks</h3>
    <p>
      Allocate, CapEdge, CreditFlow, Deal Roadshow, DealVDR, Evercall, InvestorSet, LoanSend, ResearchRoom,
      SwiftBridge, VeriSend and 17g5.com are registered
      trademarks of Finsight. Unless otherwise noted, product names, designs, logos, titles, text, images,
      audio and video within our Sites are the trademarks, service marks, trade names, copyrights or other property
      of Finsight ("Finsight's Intellectual Property").
      All other unregistered and registered trademarks are the property of their respective owners. Nothing
      contained on our Sites should be construed as granting, by implication, estoppel, or otherwise, any license or
      right to use any of Finsight’s Intellectual Property displayed on our Sites without the written permission of
      Finsight. To obtain permission, send an email to
      <a href={ `mailto:${ config.supportEmail }` }> { config.supportEmail }</a>.
    </p>
    <p>
      QuantumCF is a registered trademark of QuantumCF Inc., a wholly-owned affiliate of Finsight Group, Inc., and is
      not included among Finsight's Intellectual Property described above. QuantumCF Inc.'s use of its trademark on
      QuantumCF.com is authorized under these Terms.
    </p>
    <h3>Payments and Taxes</h3>
    <p>
      You agree to pay Finsight all amounts due in connection with your use of the Sites, as set forth in the
      applicable invoice or otherwise used or ordered by or for you through the Sites, including set up fees,
      one-time fees, non-recurring fees, overages, per-use charges, subscription fees, recurring fees, and any
      other fees and charges associated with the Sites or your account.
    </p>
    <p>
      Unless otherwise expressly specified by Finsight, all prices shown by Finsight are exclusive of taxes.
      Where applicable, taxes will be charged on the invoices issued by Finsight in accordance with applicable laws.
      Finsight, in its sole discretion, will (i) calculate the amount of taxes due, and
      (ii) may change such amounts without notice to you.
    </p>
    <p>
      For Telecommunications Services, you will be invoiced separately from software services, with both items
      clearly identified on each invoice. Charges may include per-minute, per-call, flat fees, software subscription
      fees, and applicable taxes, surcharges, and regulatory contributions.
    </p>
    <p>
      For QuantumCF.com, invoices are issued by, and payments are made directly to, QuantumCF Inc., which contracts
      with its customers separately from Finsight Group, Inc.
    </p>
    <h3>Universal Service Fund (USF)</h3>
    <p>
      SwiftBridge LLC is a registered telecommunications provider with the Federal Communications Commission (FCC) and
      remits contributions to the Universal Service Fund (USF) on eligible telecommunications revenues. You will be
      charged the applicable USF contribution factor on all eligible Telecommunications Services charges unless you
      provide a valid reseller exemption certificate. Software services are not subject to USF.
    </p>
    <h3>Limitation Of Liability</h3>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, FINSIGHT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
      CONSEQUENTIAL OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR
      INDIRECTLY, OR ANY LOSS OF DATA, USE, GOOD-WILL, OR OTHER INTANGIBLE LOSSES THAT RESULT FROM THE USE OF,
      OR THE INABILITY TO USE, THE MATERIALS ON OUR SITES OR THE PERFORMANCE OF OUR SITES, EVEN IF FINSIGHT HAS
      BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
    </p>
    <h3>
      "AS IS" and "AS AVAILABLE" Disclaimer
    </h3>
    <p>
      The Service is provided to you "AS IS" and "AS AVAILABLE"
      and with all faults and defects without warranty of any kind.
      To the maximum extent permitted under applicable law, Finsight,
      on its own behalf and on behalf of its affiliates and
      its and their respective licensors and service providers, expressly disclaims all warranties, whether express,
      implied, statutory or otherwise, with respect to the Sites, including all implied warranties of merchantability,
      fitness for a particular purpose, title and non-infringement,
      and warranties that may arise out of course of dealing,
      course of performance, usage or trade practice.
      Without limitation to the foregoing, Finsight provides no warranty or
      undertaking, and makes no representation of any kind that the Sites will meet your requirements,
      achieve any intended results, be compatible or work with any other software, applications,
      systems or services, operate without interruption, meet any performance or reliability standards or
      be error free or that any errors or defects can or will be corrected.
    </p>
    <p>
      Without limiting the foregoing, neither Finsight nor any of Our providers makes any representation or warranty of
      any kind, express or implied: (i) as to the operation or availability of the Sites, or the information, content,
      and materials or products included thereon; (ii) that the Sites will be uninterrupted or error-free;
      (iii) as to the accuracy, reliability, or currency of any information or content provided through the Sites;
      or (iv) that the Sites, its servers, the content,
      or e-mails sent from or on behalf of Finsight are free of viruses, scripts,
      trojan horses, worms, malware, timebombs or other harmful components.
    </p>
    <p>
      Some jurisdictions do not allow the exclusion of certain types of warranties or limitations on applicable
      statutory rights of a consumer, so some or all of the above exclusions and limitations may not apply to you.
      But in such a case the exclusions and limitations set forth in this section shall be applied to
      the greatest extent enforceable under applicable law.
    </p>
    <h3>Regulatory Compliance</h3>
    <p>
      You must use the Telecommunications Services in compliance with all applicable laws and regulations. Finsight
      may suspend or terminate the Telecommunications Services if required by law, regulation, or government authority.
    </p>
    <h3>Links To Other Materials</h3>
    <p>
      Our Sites may contain links to other unaffiliated websites and third-party content. These linked sites and
      third-party content are not under our control and we are not responsible for the contents of any third-party
      content, linked site or any link contained in a linked site, or any changes or updates to such sites.
      Finsight
      is providing these links and third-party content to you only as a convenience, and the inclusion of any link
      or
      third-party content does not imply endorsement by us of the site or content or the accuracy of the
      information
      contained therein.
    </p>
    <h3>Third Party Information</h3>
    <p>
      Finsight displays on its website information produced and owned by third parties (“Third Party
      Information”). Third Party Information may include ratings from credit ratings agencies. Reproduction
      and distribution of Third Party Information in any form is prohibited except with the prior written
      permission of the related third party. Third Party Information providers do not guarantee the
      accuracy, completeness, timeliness or availability of any information including ratings, and are not
      responsible for the results obtained from the use of such content. Third Party Information providers
      give no express or implied warranties, including, but not limited to, any warranties of
      merchantability or fitness of a particular purpose or use. Third Party Information providers shall not
      be liable for any direct, indirect, incidental, exemplary, compensatory, punitive, special or
      consequential damages, costs, expenses, legal fees or losses (including lost income or profits and
      opportunity costs or losses caused by negligence) in connection with any use of Third Party
      Information.
    </p>
    <p>
      Any ratings produced by a rating agency and analysis related thereto: (i) are
      solely for informational purposes only; (ii) is subject to applicable laws,
      regulations and rules and you agree to reasonably cooperate with Finsight or the
      applicable rating agency if needed to comply therewith; (iii) are mere opinions,
      not statements of fact or recommendations to purchase, hold or sell securities; the
      provision of which does not result in the applicable rating agency waiving any
      protections , privileges or defenses available under applicable laws, rules or
      regulations, including, without limitation, the First Amendment of the Constitution
      of the United States of America; (iv) do not address the suitability of securities
      or the suitability of securities for investment purposes and should not be relied
      on as investment advice; and (v) is/are not guaranteed to be accurate, compete or
      timely and are not intended to predict or guaranty future performance or
      results.
    </p>
    <h3>Content</h3>
    <p>
      Some of Our Sites allow you to post text, images, or other information that can be uploaded,
      linked to or otherwise made available by you (“Content”). You are responsible for the Content that
      you post to the Sites, including its legality, reliability, and appropriateness.
    </p>
    <p>
      By posting Content to Our Sites, you grant Us the right and license to use, modify, publicly perform,
      publicly display, reproduce, and distribute such Content on and through the Sites. You retain any and all of
      your rights to any Content you submit, post or display on or through the Sites and you are responsible
      for protecting those rights. You agree that this license includes the right for Us to make your Content
      available to other users of Our Sites, who may also use your Content subject to these Terms.
    </p>
    <p>
      You represent and warrant that: (i) the Content is yours (you own it) or you have the right to use it
      and grant Us the rights and license as provided in these Terms, and (ii) the posting of your Content
      on or through the Sites does not violate the privacy rights, publicity rights, copyrights, contract rights
      or any other rights of any person.
    </p>
    <h3>Content Restrictions</h3>
    <p>
      The Company is not responsible for the content of the Sites users. You expressly understand and
      agree that you are solely responsible for the Content and for all activity that occurs under your account,
      whether done so by you or any third person using your account.
    </p>
    <p>
      You may not transmit any Content that is unlawful, offensive, upsetting, intended to disgust, threatening,
      libelous, harmful, defamatory, obscene or otherwise objectionable.
    </p>
    <p>
      Finsight reserves the right, but not the obligation, to, in its sole discretion, determine whether
      or not any Content is appropriate and complies with these Terms and to remove any Content. Finsight
      further reserves the right to make formatting and edits and change the manner of any Content.
    </p>
    <p>
      As Finsight cannot control all content posted by users and/or third parties on the Sites, you agree to
      use the Sites at your own risk. You understand that by using the Sites you may be exposed to content
      that you may find offensive, indecent, incorrect or objectionable, and you agree that under no circumstances
      will Finsight be liable in any way for any content, including any errors or omissions in any content,
      or any loss or damage of any kind incurred as a result of your use of any Content.
    </p>
    <h3>Content Backups</h3>
    <p>
      Although regular backups of Content are performed, Finsight does not guarantee there will be no loss
      or corruption of data. You agree to maintain a complete and accurate copy of any Content
      in a location independent of the Sites.
    </p>
    <h3>Restrictions on Use with Artificial Intelligence</h3>
    <p>
      You may not use the Sites, or any data, content, or materials obtained through the Services (“Service Content”),
      for the purpose of training, fine-tuning, developing, or enhancing any artificial intelligence, machine learning,
      or automated decision-making system (“AI Systems”).
      This restriction includes, without limitation, prohibiting:
    </p>
    <ul className={ staticTemplateStyles.bulletList }>
      <li>Incorporating Service Content into datasets used for AI System training</li>
      <li>Using Service Content to improve, calibrate, or benchmark AI Systems, or</li>
      <li>Allowing any third party to do the foregoing</li>
    </ul>
    <p>
      For clarity, this does not restrict your internal use of AI Systems in ordinary business operations, provided
      such use does not involve training or developing AI Systems with Service Content.
    </p>
    <h3>Automated Access, Crawling, and Scraping</h3>
    <p>
      You may not access or collect data from the Sites via any automated means (bots, scrapers, crawlers, spiders,
      scripts, or similar) unless expressly permitted in writing by Finsight. The prohibition includes crawling,
      scraping, indexing, data mining, or bulk extraction, even if publicly accessible. You may not circumvent rate
      limits, CAPTCHAs, IP blocks, or other protective measures. Violations may lead to immediate suspension or
      termination of your access, injunctive relief, and any other rights or remedies available to Finsight.
    </p>
    <h3>Copyright Policy</h3>
    <p>
      These Terms do not grant you a license to use any trademark of Finsight or its affiliates. You further
      agree not to use, change or delete any proprietary notices from materials downloaded from Finsight.
    </p>
    <p>
      You understand that Finsight has no affiliation with the U.S. Securities and Exchange Commission (SEC)
      or any of its affiliated entities, and claims no intellectual property or other rights over
      company filing content or data provided by the SEC.
    </p>
    <p>
      All company names, logos, and brands are property of their respective owners. All company, product
      and service names used in these Sites are for identification purposes only. Use of these names,
      logos, and brands does not imply endorsement.
    </p>
    <h3>DMCA Notice and DMCA Procedure for Copyright Infringement Claims</h3>
    <p>
      You may submit a notification pursuant to the Digital Millennium Copyright Act (DMCA) by providing our
      Copyright Agent with the following information in writing (see 17 U.S.C 512(c)(3) for further detail):
    </p>
    <p>
      An electronic or physical signature of the person authorized
      to act on behalf of the owner of the copyright's interest.
    </p>
    <p>
      A description of the copyrighted work that you claim has been infringed,
      including the URL (i.e., web page address) of the location where the copyrighted work exists or
      a copy of the copyrighted work.
    </p>
    <p>
      Identification of the URL or other specific location on the Sites
      where the material that you claim is infringing is located.
    </p>
    <p>
      Your address, telephone number, and email address.
    </p>
    <p>
      A statement by you that you have a good faith belief that the disputed use is
      not authorized by the copyright owner, its agent, or the law.
    </p>
    <p>
      A statement by you, made under penalty of perjury, that the above information in your notice
      is accurate and that you are the copyright owner or authorized to act on the copyright owner's behalf.
    </p>
    <p>
      You can contact our copyright agent via email at legal@finsight.com Upon receipt of a notification,
      Finsight will take whatever action, in its sole discretion, it deems appropriate,
      including removal of the challenged content from Our Sites.
    </p>
    <h3>Americans with Disabilities Act (ADA) Compliance</h3>
    <p>
      These Sites are for authorized users only and are not subject to ADA compliance requirements as they are not
      intended for public access.
    </p>
    <h3>Indemnification</h3>
    <p>
      You agree to indemnify and hold harmless Finsight and its officers, directors, employees and agents, from
      and against any claims, suits, proceedings, disputes, demands, liabilities, damages, losses, costs and
      expenses,
      including, without limitation, reasonable legal and accounting fees (including costs of defense
      of claims, suits or proceedings brought by third parties), in any way related to (a) your access
      to or use of our Sites, or (b) your breach of any of these Terms.
    </p>
    <h3>Governing Law And Jurisdiction</h3>
    <p>
      Your use of our Sites shall be governed in all respects by the laws of the state of New York,
      U.S.A., without regard to choice of law provisions. You agree that jurisdiction over and venue in any
      legal proceeding directly or indirectly arising out of or relating to use of our Sites shall be in the
      state or federal courts located in New York County, New York.
    </p>
    <p>
      Our Sites are controlled and operated from the United States, and we make no representations that they are
      appropriate or available for use in other locations.
    </p>
    <h3>Termination</h3>
    <p>Finsight may terminate or suspend this license at any time, with or without cause or notice to you.</p>
    <h3>Your Feedback to Us</h3>
    <p>
      You assign all rights, title and interest in any feedback you provide to Us. If for any reason such assignment is
      ineffective, you agree to grant the Company a non-exclusive, perpetual, irrevocable, royalty free,
      worldwide right and license to use, reproduce, disclose, sub-license, distribute,
      modify and exploit such feedback without restriction.
    </p>
    <h3>United States Legal Compliance</h3>
    <p>
      You represent and warrant that (i) You are not located in a country that is subject to the United States
      government embargo, or that has been designated by the United States government as a "terrorist supporting"
      country, and (ii) You are not listed on any United States government list of prohibited or restricted parties.
    </p>
    <h3>Severability</h3>
    <p>
      If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and
      interpreted to accomplish the objectives of such provision to the greatest extent possible
      under applicable law and the remaining provisions will continue in full force and effect.
    </p>
    <h3>Waiver</h3>
    <p>
      Except as provided herein, the failure of either party to enforce any of these Terms shall not be
      construed as a waiver or limitation of the party’s right to subsequently enforce and
      compel strict compliance with every provision of this agreement.
    </p>
    <h3>General</h3>
    <p>
      Finsight may revise these terms at any time by updating this posting. You should visit this page from time
      to time to review the then-current terms because they are binding on you. Certain provisions of these terms
      may be superseded by expressly designated legal notices or terms located on particular pages of our
      Sites.
    </p>
    <h3>Contacting Us</h3>
    <p>
      If you have any questions about these Terms, the practices of our Sites, or your dealings with our Sites,
      please contact us at legal@finsight.com.
    </p>
    <h3>Thomson Reuters Notice And Disclaimer (Where Applicable)</h3>
    <p>
      Copyright © 1999 - { getYear(new Date()) }, Thomson Reuters. All rights reserved. Thomson Reuters (Markets) LLC
      (“Thomson Reuters”) and its affiliates are referred to below as “Thomson Reuters”.
    </p>
    <p>
      The “Information Product” is any data or service provided by Thomson Reuters. Thomson Reuters or its third
      party providers own and retain all rights, title and interest, including but not limited to copyright,
      trademarks, patents, database rights, trade secrets, know-how, and all other intellectual property rights
      or forms of protection of similar nature or having equivalent effect, anywhere in the world, in the
      Information Product and user is not granted any proprietary interest therein or thereto. The Information
      Product constitutes confidential and trade secrets of Thomson Reuters or its third party providers.
      Display, performance, reproduction, distribution of, or creation of derivative works or improvements from
      Information Product in any form or manner is expressly prohibited, except to the extent expressly permitted
      hereunder, or otherwise, with the prior written permission of Thomson Reuters.
    </p>
    <p>
      To the extent that the Information Product contains any third party data referred to in
      the General Restrictions/Notices page set forth on&nbsp;
      <a
        href="http://www.thomsonreuters.com/datause"
        target="_blank"
      >
        http://www.thomsonreuters.com/datause
      </a>,
      the terms set forth on such General Restrictions/Notices page shall apply to user.
    </p>
    <p>
      NEITHER THOMSON REUTERS NOR ITS THIRD PARTY PROVIDERS WARRANT THAT THE PROVISION OF THE INFORMATION PRODUCT
      WILL BE UNINTERRUPTED, ERROR FREE, TIMELY, COMPLETE OR ACCURATE, NOR DO THEY MAKE ANY WARRANTIES AS TO THE
      RESULTS TO BE OBTAINED FROM USE OF THE SAME. USE OF THE INFORMATION PRODUCT AND RELIANCE THEREON IS AT
      USER’S SOLE RISK. NEITHER THOMSON REUTERS OR ITS THIRD PARTY PROVIDERS WILL IN ANY WAY BE LIABLE TO USER OR
      ANY OTHER ENTITY OR PERSON FOR THEIR INABILITY TO USE THE INFORMATION PRODUCT, OR FOR ANY INACCURACIES,
      ERRORS, OMISSIONS, DELAYS, COMPUTER VIRUS OR OTHER INFIRMITY OR CORRUPTION, DAMAGES, CLAIMS, LIABILITIES OR
      LOSSES, REGARDLESS OF CAUSE, IN OR ARISING FROM THE USE OF THE INFORMATION PRODUCT. THE INFORMATION PRODUCT
      IS PROVIDED ON AN “AS IS” BASIS AND WITHOUT WARRANTY OF ANY KIND. NO WARRANTIES EITHER EXPRESSED OR
      IMPLIED, INCLUDING BUT NOT LIMITED TO ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
      PURPOSE, TITLE, INFRINGEMENT OR OTHERWISE IS PROVIDED HEREUNDER.
    </p>
    <p>
      IN NO EVENT WILL THOMSON REUTERS OR ITS THIRD PARTY PROVIDERS BE LIABLE FOR ANY DAMAGES, INCLUDING WITHOUT
      LIMITATION DIRECT OR INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES, LOSSES OR
      EXPENSES ARISING IN CONNECTION WITH INFORMATION PRODUCT EVEN IF THOMSON REUTERS OR ITS THIRD PARTY PROVIDERS
      OR THEIR REPRESENTATIVES ARE ADVISED OF THE POSSIBILITY OF SUCH DAMAGES, LOSSES OR EXPENSES. FURTHER,
      THOMSON
      REUTERS OR ITS PARTY PROVIDERS SHALL NOT BE LIABLE IN ANY MANNER FOR REDISTRIBUTOR’S PRODUCTS OR SERVICES.
    </p>
    <h3>CUSIP Database (Where Applicable)</h3>
    <p>
      You agree and acknowledge that the CUSIP Database and the information contained therein is and shall remain
      valuable intellectual property owned by, or licensed to, CUSIP Global Services ("CGS") and the American Bankers
      Association ("ABA"), and that no proprietary rights are being transferred to you in such materials or in any of
      the information contained therein. Any use by you outside of the clearing and settlement of transactions
      requires a license from CGS, along with an associated fee based on usage.
    </p>
    <p>
      You agree that misappropriation or misuse of such materials may cause serious damage to CGS and ABA, and that in
      such event money damages may not constitute sufficient compensation to CGS and ABA; consequently, you agree that
      in the event of any misappropriation or misuse, CGS and ABA shall have the right to obtain injunctive relief in
      addition to any other legal or financial remedies to which CGS and ABA may be entitled.
    </p>
    <p>
      You agree not to publish or distribute in any medium the CUSIP Database or any information contained therein or
      summaries or subsets thereof to any person or entity except in connection with the normal clearing and
      settlement of security transactions. You further agree that the use of CUSIP numbers and descriptions is not
      intended to create or maintain, and does not serve the purpose of the creation or maintenance of, a master file
      or database of CUSIP descriptions or numbers for itself or any third party recipient of such service and is not
      intended to create and does not serve in any way as a substitute for the CUSIP MASTER TAPE, PRINT, DB, INTERNET,
      ELECTRONIC, CD-ROM Services and/or any other future services developed by the CGS.
    </p>
    <p>
      NEITHER CGS, ABA NOR ANY OF THEIR AFFILIATES MAKE ANY WARRANTIES, EXPRESS OR IMPLIED, AS TO THE ACCURACY,
      ADEQUACY OR COMPLETENESS OF ANY OF THE INFORMATION CONTAINED IN THE CUSIP DATABASE. ALL SUCH MATERIALS ARE
      PROVIDED TO USER ON AN "AS IS" BASIS, WITHOUT ANY WARRANTIES AS TO MERCHANTABILITY OR FITNESS FOR A PARTICULAR
      PURPOSE OR USE NOR WITH RESPECT TO THE RESULTS WHICH MAY BE OBTAINED FROM THE USE OF SUCH MATERIALS. NEITHER
      CGS, ABA NOR THEIR AFFILIATES SHALL HAVE ANY RESPONSIBILITY OR LIABILITY FOR ANY ERRORS OR OMISSIONS NOR SHALL
      THEY BE LIABLE FOR ANY DAMAGES, WHETHER DIRECT OR INDIRECT, SPECIAL OR CONSEQUENTIAL, EVEN IF THEY HAVE BEEN
      ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IN NO EVENT SHALL THE LIABILITY OF CGS, ABA OR ANY OF THEIR
      AFFILIATES PURSUANT TO ANY CAUSE OF ACTION, WHETHER IN CONTRACT, TORT, OR OTHERWISE, EXCEED THE FEE PAID BY USER
      FOR ACCESS TO SUCH MATERIALS IN THE MONTH IN WHICH SUCH CAUSE OF ACTION IS ALLEGED TO HAVE ARISEN. FURTHERMORE,
      CGS AND ABA SHALL HAVE NO RESPONSIBILITY OR LIABILITY FOR DELAYS OR FAILURES DUE TO CIRCUMSTANCES BEYOND THEIR
      CONTROL.
    </p>
    <p>
      You agree that the foregoing terms and conditions shall survive any termination of its right of access to the
      materials identified above.
    </p>
    <p>
      In the event you have an appropriate agreement with CGS covering your use of the CUSIP Database (the "Separate
      Agreement"), your use of the CUSIP Database shall be governed by the terms of the Separate Agreement for so long
      as it remains in effect.
    </p>
    <h3>CreditFlow Indices (CFRx)</h3>
    <p>
      The CreditFlow Index (&quot;CFRx&quot;) and all associated market data, methodologies, and intellectual
      property are owned exclusively by CreditFlow, a division of Finsight Group Inc. All rights are reserved.
      For licensing inquiries, please contact us directly at{ ' ' }
      <a href="mailto:legal@finsight.com">legal@finsight.com</a>.
    </p>
    <h4>Proper Attribution &amp; Reference Guidelines</h4>
    <p>
      When referencing the index, please adhere to the following standards:
    </p>
    <ul className={ staticTemplateStyles.bulletList }>
      <li>The index should be referenced as &quot;CFRx&quot; or &quot;CreditFlow Indices&quot;</li>
      <li>Always attribute ownership to CreditFlow, a division of Finsight Group Inc.</li>
    </ul>
    <p>
      Example attribution: &quot;CFRx is a trademark of CreditFlow, a division of Finsight Group Inc.&quot;
    </p>
    <h4>Data Usage &amp; Reproduction</h4>
    <p>
      <strong><em>Quoting Data</em></strong>
      <br />
      When quoting CreditFlow data, all statistics must be used verbatim as they appear on CreditFlow.
      Modification, adjustment, or recharacterization of published data is not permitted without prior
      written consent.
    </p>
    <p>
      <strong><em>Reproduction &amp; Redistribution</em></strong>
      <br />
      To reproduce or redistribute CreditFlow materials in their entirety, please submit your request
      to <a href="mailto:legal@finsight.com">legal@finsight.com</a>. Unauthorized reproduction or
      redistribution is strictly prohibited.
    </p>
    <h4>Third-Party Product Development</h4>
    <p>
      Third parties must obtain explicit written permission from CreditFlow to:
    </p>
    <ul className={ staticTemplateStyles.bulletList }>
      <li>Create financial products linked to CFRx</li>
      <li>
        Develop derived products, including but not limited to ETFs, mutual funds, or structured products
      </li>
      <li>Use the CFRx as a benchmark for performance measurement in commercial offerings</li>
    </ul>
    <p>
      To inquire about product development permissions, contact{ ' ' }
      <a href="mailto:legal@finsight.com">legal@finsight.com</a>.
    </p>
    <h4>Geographic Scope &amp; Coverage</h4>
    <p>
      The current iteration of the CFRx applies exclusively to U.S. new issue fixed income data sets.
      Additional index iterations covering EMEA, APAC, LATAM, and emerging markets are planned for future
      release. For updates on expanded coverage, please contact CreditFlow directly.
    </p>
    <h4>Disclaimer</h4>
    <p>
      CFRx and CreditFlow Indices are trademarks of CreditFlow, a division of Finsight Group Inc. The
      information provided herein is for informational purposes only and does not constitute investment
      advice, an offer to sell, or a solicitation of an offer to buy any security or financial product.
      Past performance of the index is not indicative of future results.
    </p>
    <p>
      Third-party use of the CFRx name, data, or methodology does not imply sponsorship, endorsement, or
      affiliation with CreditFlow or Finsight Group Inc. unless expressly stated in a written agreement.
    </p>
    <h4>Contact</h4>
    <p>
      For all licensing, reproduction, and partnership inquiries:
      <br />
      <strong>Email:</strong> <a href="mailto:legal@finsight.com">legal@finsight.com</a>
    </p>
    <p>Last Updated: September 10, 2026</p>
  </>
);

export default Terms;
